/**
 * city/buildings/tube-house-shops.js
 * Dựng nhà phố thương mại dịch vụ & quán cà phê phố cổ Việt Nam
 */

import * as THREE from 'three';
import { createRng, hashString, shade } from '../builder-core.js';
import { CANVAS_FONTS } from '../../core/fonts.js';

class MethodsHolder {
  // 3.3 NHÀ THUỐC 4 TẦNG: biển vẫy dọc vuông góc mặt phố, ban công cây rủ, sân thượng mái tôn
  createTubeHouseCommercial(options = {}) {
    const house = new THREE.Group();
    house.name = 'TubeHouse_Commercial_VN';
    const w = options.width || 4.0;
    const d = options.depth || 5.8;
    const G = 3.6,
      F = 3.0,
      H = G + 3 * F;
    const fz = d / 2;
    const storeName = options.storeName || 'Nhà Thuốc 24h';
    const accent = options.signColor || '#0284c7';
    const wall = options.wallColor || '#ffffff';
    const seed = hashString('commercial|' + storeName);
    const rnd = createRng(seed);

    const frontMat = this._houseBody(house, w, H, d, wall, seed, 0.45);
    const accentMat = this.mat(accent, { roughness: 0.6 });
    const alu = this.mat('#e5e7eb', { roughness: 0.4, metalness: 0.5 });

    // TẦNG TRỆT: tủ thuốc sáng đèn sau cửa kính + hộp cửa cuốn
    const shopW = w - 0.5,
      shopH = 2.7;
    this.box(
      house,
      shopW,
      shopH,
      0.04,
      this.texMat(this.getInteriorTexture('pharmacy', seed), {}, true),
      0,
      shopH / 2,
      fz + 0.02,
      null,
      false
    );
    this.box(house, shopW + 0.1, 0.07, 0.1, alu, 0, 0.035, fz + 0.05);
    [-1, 1].forEach((s) => {
      this.box(house, 0.08, shopH, 0.1, alu, s * (shopW / 2 + 0.01), shopH / 2, fz + 0.05);
      this.box(house, 0.05, shopH, 0.08, alu, s * 0.6, shopH / 2, fz + 0.05, null, false);
    });
    this.box(
      house,
      shopW + 0.14,
      0.24,
      0.3,
      this.mat('#a1a1aa', { metalness: 0.4, roughness: 0.5 }),
      0,
      shopH + 0.12,
      fz + 0.13
    );
    this._addSign(house, {
      text: storeName,
      sub: options.signSub || 'Tư vấn miễn phí — ĐT: 0987 654 321',
      bg: accent,
      fg: '#ffffff',
      icon: 'cross',
      w: w - 0.1,
      h: 0.62,
      y: 3.2,
      z: fz + 0.09,
      depth: 0.14
    });
    this.box(house, w + 0.06, 0.16, 0.22, accentMat, 0, G, fz + 0.11);

    // BIỂN VẪY DỌC vuông góc mặt tiền (đọc được khi nhìn dọc phố)
    const letters = (options.verticalText || storeName.replace(/\s*24h$/i, '')).toUpperCase();
    const vTex = this._tex(`vsign|${letters}|${accent}`, 128, 768, (ctx, cw, ch) => {
      const g = ctx.createLinearGradient(0, 0, cw, 0);
      g.addColorStop(0, shade(accent, -0.15));
      g.addColorStop(0.5, shade(accent, 0.15));
      g.addColorStop(1, shade(accent, -0.15));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, cw, ch);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 6;
      ctx.strokeRect(8, 8, cw - 16, ch - 16);
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `bold 64px ${CANVAS_FONTS.sans}`;
      const chars = [...letters];
      const stepY = (ch - 60) / Math.max(chars.length, 1);
      chars.forEach((c, i) => ctx.fillText(c, cw / 2, 30 + stepY * (i + 0.5)));
    });
    const vFace = this.texMat(vTex, {}, true);
    const vEdge = this.mat(shade(accent, -0.3));
    const vH = 3 * F - 0.8;
    this.box(
      house,
      0.16,
      vH,
      0.8,
      [vFace, vFace, vEdge, vEdge, vEdge, vEdge],
      -w / 2 + 0.28,
      G + 0.4 + vH / 2,
      fz + 0.5
    );
    [G + 0.6, G + vH].forEach((ay) => this.box(house, 0.05, 0.05, 0.5, alu, -w / 2 + 0.28, ay, fz + 0.2));

    // TẦNG 2-4: cửa sổ khung nhôm, ban công lan can sắt, chậu cây rủ, điều hòa
    for (let i = 0; i < 3; i++) {
      const fy = G + i * F;
      this._addWindow(house, {
        x: 0.3,
        y: fy + 1.45,
        z: fz + 0.01,
        w: 2.3,
        h: 1.9,
        frame: '#e5e7eb',
        cols: 3,
        sill: false,
        glass: i === 1 ? 'home' : 'sky'
      });
      this._addBalcony(house, {
        x: 0.3,
        y: fy + 0.08,
        z: fz,
        w: w - 0.9,
        depth: 0.7,
        rail: 'iron',
        railColor: '#1f2937',
        slabColor: '#e5e7eb'
      });
      this._addPlanter(house, 0.3 + (rnd() - 0.5) * 0.8, fy + 1.03, fz + 0.62, 1.0 + rnd() * 0.6, rnd, true);
      if (i !== 1) this._addAcUnit(house, w / 2 - 0.62, fy + 0.4, fz + 0.26);
      this.box(house, w + 0.06, 0.14, 0.18, this.mat(shade(accent, 0.6)), 0, fy + F, fz + 0.09);
    }
    // Quần áo phơi tầng 3
    this.box(house, 1.2, 0.012, 0.012, this.mat('#27272a'), w / 2 - 0.9, G + F + 1.9, fz + 0.45, null, false);
    [
      ['#ef4444', 0.25],
      ['#fafafa', 0.55],
      ['#0ea5e9', 0.85]
    ].forEach(([c, off]) => {
      this.box(house, 0.24, 0.34, 0.03, this.mat(c, { roughness: 0.9 }), w / 2 - 1.5 + off, G + F + 1.7, fz + 0.45);
    });

    // SÂN THƯỢNG: tường chắn, khung sắt mái tôn, bồn nước
    this.box(house, w, 0.7, 0.16, frontMat, 0, H + 0.35, fz - 0.08);
    this.box(house, w + 0.08, 0.08, 0.24, accentMat, 0, H + 0.74, fz - 0.08);
    [-1, 1].forEach((s) =>
      this.box(house, 0.14, 0.7, d - 0.2, this.mat('#a8a29e'), s * (w / 2 - 0.07), H + 0.35, -0.1)
    );
    const steel = this.mat('#52525b', { metalness: 0.6, roughness: 0.4 });
    [-1, 1].forEach((s) =>
      [fz - 0.3, -fz + 0.4].forEach((pz) => this.box(house, 0.08, 2.3, 0.08, steel, s * (w / 2 - 0.12), H + 1.15, pz))
    );
    this.box(
      house,
      w + 0.2,
      0.05,
      d - 0.2,
      this.texMat(this._repeat(this.getToleTexture('#1d4ed8'), 3, 1), { metalness: 0.4, roughness: 0.45 }),
      0,
      H + 2.35,
      -0.05,
      [0.06, 0, 0]
    );
    this._addWaterTank(house, -w * 0.15, H, -d * 0.25);

    return house;
  }

  // 3.4 QUÁN CÀ PHÊ HỘI AN: tường vàng, cửa bức bàn, mái hiên ngói, đèn lồng, ban công gỗ hoa giấy, mái 2 dốc
  createTubeHouseCafe(options = {}) {
    const house = new THREE.Group();
    house.name = 'TubeHouse_Cafe_VN';
    const w = options.width || 4.2;
    const d = options.depth || 6.0;
    const G = 3.8,
      F = 2.8,
      H = G + F;
    const fz = d / 2;
    const storeName = options.storeName || 'Cà Phê Sáng';
    const wall = options.wallColor || '#f5b82e';
    const seed = hashString('cafe|' + storeName);
    const rnd = createRng(seed);

    this._houseBody(house, w, H, d, wall, seed, 0.95);
    const wood = this.mat('#5b3413', { roughness: 0.7 });
    const panel = this.texMat(this.getWoodPanelTexture('#7a4a22'), { roughness: 0.7 });
    const tile = this.getRoofTileTexture();

    // TẦNG TRỆT: khung gỗ, cửa bức bàn gấp 2 bên, quán sáng đèn bên trong
    const openW = w - 0.9,
      doorH = 2.6;
    this.box(
      house,
      openW,
      doorH,
      0.04,
      this.texMat(this.getInteriorTexture('cafe', seed), {}, true),
      0,
      doorH / 2,
      fz + 0.02,
      null,
      false
    );
    this.box(house, openW + 0.28, 0.16, 0.16, wood, 0, doorH + 0.08, fz + 0.08);
    [-1, 1].forEach((s) => {
      this.box(house, 0.14, doorH, 0.16, wood, s * (openW / 2 + 0.07), doorH / 2, fz + 0.08);
      this.box(house, 0.3, doorH - 0.08, 0.05, panel, s * (openW / 2 - 0.17), (doorH - 0.08) / 2, fz + 0.1, [
        0,
        s * 0.35,
        0
      ]);
      this.box(house, 0.3, doorH - 0.08, 0.05, panel, s * (openW / 2 - 0.42), (doorH - 0.08) / 2, fz + 0.2, [
        0,
        -s * 0.35,
        0
      ]);
    });
    this.box(house, w - 0.1, 0.12, 0.4, this.mat('#78716c', { roughness: 0.9 }), 0, 0.06, fz + 0.2);

    // Mái hiên ngói âm dương trên con-sơn gỗ
    const cDepth = 1.05,
      cDrop = 0.32,
      cTop = 2.92;
    const cLen = Math.hypot(cDepth, cDrop);
    const canopyMat = this.texMat(this._repeat(tile, w, 1.1), { roughness: 0.85 });
    this.box(house, w + 0.1, 0.1, cLen, canopyMat, 0, cTop - cDrop / 2, fz + cDepth / 2, [
      Math.atan2(cDrop, cDepth),
      0,
      0
    ]);
    this.box(house, w + 0.12, 0.1, 0.14, this.mat('#7c2d12'), 0, cTop + 0.03, fz + 0.06);
    this.box(house, w + 0.1, 0.08, 0.04, wood, 0, cTop - cDrop - 0.07, fz + cDepth + 0.01);
    [-1, 1].forEach((s) =>
      this.box(house, 0.08, 0.08, 1.0, wood, s * (w / 2 - 0.3), cTop - 0.5, fz + 0.48, [-0.26, 0, 0])
    );
    ['#ef4444', '#facc15', '#ef4444', '#f97316'].forEach((c, i) => {
      this._addLantern(house, -1.5 + i, cTop - cDrop - 0.5, fz + 0.8, c);
    });

    // Biển gỗ thư pháp phía trên mái hiên
    this._addSign(house, {
      text: storeName,
      bg: '#3b2410',
      fg: '#fde68a',
      border: '#b45309',
      font: CANVAS_FONTS.serif,
      w: w - 0.8,
      h: 0.52,
      y: cTop + 0.4,
      z: fz + 0.05,
      depth: 0.08
    });

    // TẦNG 2: ban công gỗ, 2 cửa chớp xanh ngọc, hoa giấy rủ
    this._addBalcony(house, {
      y: G,
      z: fz,
      w: w - 0.2,
      depth: 0.9,
      slabColor: '#5b3413',
      rail: 'wood',
      railColor: '#5b3413'
    });
    [-0.95, 0.95].forEach((wx) =>
      this._addWindow(house, {
        x: wx,
        y: G + 1.2,
        z: fz + 0.01,
        w: 0.8,
        h: 1.8,
        frame: '#5b3413',
        shutters: options.shutterColor || '#1d6f6a',
        sill: false,
        cols: 2,
        rows: 2,
        glass: 'home'
      })
    );
    this._addBougainvillea(house, -w * 0.18, G + 1.0, fz + 0.93, w * 0.55, 1.5, rnd);
    this._addBougainvillea(house, -w / 2 + 0.25, H - 0.1, fz + 0.05, 0.6, 2.4, rnd);
    this._addPlanter(house, 1.4, G, fz + 0.55, 0.45, rnd);

    // MÁI NGÓI 2 DỐC: dốc trước hướng ra phố, bờ nóc, đầu đao, hồi tam giác
    const ridgeY = H + 1.25;
    const frontEaveZ = fz + 0.55,
      backEaveZ = -fz - 0.3;
    const fLen = Math.hypot(frontEaveZ, ridgeY - H);
    const bLen = Math.hypot(-backEaveZ, ridgeY - H);
    this.box(
      house,
      w + 0.3,
      0.14,
      fLen,
      this.texMat(this._repeat(tile, w + 0.3, fLen), { roughness: 0.85 }),
      0,
      (H + ridgeY) / 2,
      frontEaveZ / 2,
      [Math.atan2(ridgeY - H, frontEaveZ), 0, 0]
    );
    this.box(
      house,
      w + 0.3,
      0.14,
      bLen,
      this.texMat(this._repeat(tile, w + 0.3, bLen), { roughness: 0.85 }),
      0,
      (H + ridgeY) / 2,
      backEaveZ / 2,
      [-Math.atan2(ridgeY - H, -backEaveZ), 0, 0]
    );
    const ridge = this.mat('#7c2d12', { roughness: 0.8 });
    this.box(house, w + 0.4, 0.18, 0.3, ridge, 0, ridgeY + 0.06, 0);
    [-1, 1].forEach((s) => {
      this.box(house, 0.22, 0.3, 0.3, ridge, s * (w / 2 + 0.15), ridgeY + 0.2, 0);
      this.box(house, 0.14, 0.14, 0.2, ridge, s * (w / 2 + 0.24), ridgeY + 0.38, 0, [0, 0, s * -0.5]);
    });
    const gable = new THREE.Shape();
    gable.moveTo(-fz, 0);
    gable.lineTo(fz, 0);
    gable.lineTo(0, ridgeY - H);
    gable.closePath();
    const gableGeo = this._geo(
      `gable|${fz}|${ridgeY - H}`,
      () => new THREE.ExtrudeGeometry(gable, { depth: 0.15, bevelEnabled: false })
    );
    const gableMat = this.mat(shade(wall, -0.12), { roughness: 0.9 });
    [-1, 1].forEach((s) => {
      const m = new THREE.Mesh(gableGeo, gableMat);
      m.rotation.y = Math.PI / 2;
      m.position.set(s * (w / 2 - 0.08) - 0.075, H, 0);
      m.castShadow = true;
      m.receiveShadow = true;
      house.add(m);
    });

    // BÀN GHẾ NHỰA & DÙ VỈA HÈ
    this._addStreetSeating(house, -1.0, fz + 1.9, rnd, true);
    this._addStreetSeating(house, 1.2, fz + 1.5, rnd, false);

    return house;
  }
}

export function registerTubeHouseShopsMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
